// src/scenes/car-operations/CreateCarOperation.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";

/* ---------- Axios (CRA: REACT_APP_API_URL may include /api) ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. https://example.com/api
  timeout: 15000,
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

/* ---------- Helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    if (f.default !== undefined) acc[f.name] = f.default;
    else if (f.type === "checkbox") acc[f.name] = false;
    else if (f.type === "number") acc[f.name] = "";
    else acc[f.name] = "";
    return acc;
  }, {});

function carDisplay(c) {
  if (!c) return "-";
  const name = c.carName ?? c.name ?? `#${c.id}`;
  const plate = c.carPlate ?? c.plate ?? "";
  return plate ? `${name} — plate — ${plate}` : `${name}`;
}

const OP_SCHEMA = yup.object().shape({
  currentMileage: yup.number().typeError("enter a number").required("required"),
  previousMileage: yup
    .number()
    .typeError("enter a number")
    .required("required"),
  actionType: yup
    .string()
    .oneOf(["fuel", "service", "assign"], "invalid action")
    .required("required"),

  // Fuel-only: required
  previousFuel: yup
    .number()
    .typeError("enter a number")
    .when("actionType", ([t], s) =>
      t === "fuel" ? s.required("required") : s.notRequired()
    ),
  currentFuel: yup
    .number()
    .typeError("enter a number")
    .when("actionType", ([t], s) =>
      t === "fuel" ? s.required("required") : s.notRequired()
    ),

  // Service: optional
  serviceCost: yup
    .number()
    .typeError("enter a number")
    .notRequired()
    .nullable(),
  serviceDate: yup.date().typeError("invalid date").notRequired().nullable(),

  // Assign: optional
  assignDate: yup.date().typeError("invalid date").notRequired().nullable(),

  // Relations
  carId: yup.number().typeError("select a car").required("required"),
  userId: yup.number().typeError("invalid user").nullable(),
});

/* ---------- API helpers ---------- */
async function getUsers() {
  const res = await api.get("/api/user", { params: { _t: Date.now() } });
  return res.data; // expects array
}
async function getCars() {
  const res = await api.get("/api/carInfo", { params: { _t: Date.now() } });
  return res.data; // expects array
}
async function postCarOperation(body) {
  try {
    const res = await api.post("/api/car-operation", body);
    return { data: res.data, error: null };
  } catch (err) {
    const responseData = err?.response?.data;
    const message =
      responseData?.error?.errors?.[0]?.message ||
      responseData?.message ||
      err.message ||
      "Request failed";
    return { data: responseData, error: message };
  }
}

/* ---------- Page ---------- */
export default function CreateCarOperation() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [cars, setCars] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // local mirror of actionType so we can hide fields via sx
  const [actionType, setActionType] = useState("fuel");

  const mountedRef = useRef(true);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const [u, c] = await Promise.allSettled([getUsers(), getCars()]);
      if (!mountedRef.current) return;

      if (u.status === "fulfilled" && Array.isArray(u.value)) setUsers(u.value);
      else setUsers([]);

      if (c.status === "fulfilled" && Array.isArray(c.value)) setCars(c.value);
      else setCars([]);
    } catch {
      if (mountedRef.current) {
        setUsers([]);
        setCars([]);
        setToast({ open: true, msg: "Failed to load dropdowns", sev: "error" });
      }
    } finally {
      if (mountedRef.current) setLoadingLists(false);
    }
  };

  // initial fetch + refetch on focus + periodic refetch
  useEffect(() => {
    mountedRef.current = true;
    loadLists();

    const onFocus = () => loadLists();
    window.addEventListener("focus", onFocus);

    const intervalId = setInterval(loadLists, 30000); // 30s

    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", onFocus);
      clearInterval(intervalId);
    };
  }, []);

  // Build fields (visibility controlled only for serviceCost/serviceDate/assignDate)
  const FIELDS = useMemo(() => {
    const hasUsers = users.length > 0;
    const hasCars = cars.length > 0;

    const userOptions = [{ label: "None", value: "" }].concat(
      (users || [])
        .filter((u) => String(u?.Role?.name || "").toLowerCase() === "sales")

        .sort((a, b) =>
          String(a?.fullName || a?.userName || a?.name || "").localeCompare(
            String(b?.fullName || b?.userName || b?.name || "")
          )
        )
        .map((u) => ({
          label: u.fullName ?? u.name ?? u.userName ?? `User #${u.id}`,
          value: u.id,
        }))
    );

    const carOptions = (cars || []).map((c) => ({
      label: carDisplay(c),
      value: c.id,
    }));

    return [
      {
        name: "actionType",
        label: "Action Type",
        type: "select",
        span: 2,
        required: true,
        options: [
          { label: "Fuel", value: "fuel" },
          { label: "Service", value: "service" },
          { label: "Assign", value: "assign" },
        ],
        // capture into local state to drive visibility
        onChange: (e, bag) => {
          bag.handleChange(e);
          setActionType(e.target.value);
        },
        default: "",
      },

      // Relations
      {
        name: "carId",
        label: loadingLists
          ? "Loading cars..."
          : hasCars
          ? "Car"
          : "No cars found",
        type: "select",
        span: 2,
        required: true,
        options: carOptions,
        props: { disabled: loadingLists || !hasCars },
      },
      {
        name: "userId",
        label: loadingLists
          ? "Loading users..."
          : hasUsers
          ? "User (optional)"
          : "No users found",
        type: "select",
        span: 2,
        required: false,
        options: userOptions,
        props: {
          disabled: loadingLists || (!hasUsers && userOptions.length === 1),
        },
        default: "",
      },

      // Mileage (always visible)
      {
        name: "currentMileage",
        label: "Current Mileage",
        type: "number",
        span: 2,
        required: true,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "previousMileage",
        label: "Previous Mileage",
        type: "number",
        span: 2,
        required: true,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },

      // Fuel fields — fixed/always visible (validation still fuel-only)
      {
        name: "previousFuel",
        label: "Previous Fuel",
        type: "number",
        span: 2,
        required: false,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "currentFuel",
        label: "Current Fuel",
        type: "number",
        span: 2,
        required: false,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },

      // Service (visible only for service)
      {
        name: "serviceCost",
        label: "Service Cost (Service action)",
        type: "number",
        span: 2,
        required: false,
        props: {
          placeholder: "e.g. 50.0",
          inputProps: { step: "0.1" },
          sx: { display: actionType === "service" ? undefined : "none" },
          onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
        },
      },
      {
        name: "serviceDate",
        label: "Service Date (Service action)",
        type: "datetime-local",
        span: 2,
        required: false,
        props: {
          InputLabelProps: { shrink: true },
          sx: { display: actionType === "service" ? undefined : "none" },
        },
      },

      // Assign (visible only for assign)
      {
        name: "assignDate",
        label: "Assign Date (Assign action)",
        type: "datetime-local",
        span: 2,
        required: false,
        props: {
          InputLabelProps: { shrink: true },
          sx: { display: actionType === "assign" ? undefined : "none" },
        },
      },
    ];
  }, [users, cars, loadingLists, actionType]);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, resetForm }
  ) => {
    // Build payload based on actionType, omitting irrelevant optionals entirely
    const payload = {
      currentMileage: Number(values.currentMileage),
      previousMileage: Number(values.previousMileage),
      actionType: values.actionType,
      carId: Number(values.carId),
      // userId: optional -> null if not selected (per your Postman)
      userId:
        values.userId === "" || values.userId === undefined
          ? null
          : Number(values.userId),
    };

    if (values.actionType === "fuel") {
      if (values.previousFuel !== "" && values.previousFuel !== undefined) {
        payload.previousFuel = Number(values.previousFuel);
      }
      if (values.currentFuel !== "" && values.currentFuel !== undefined) {
        payload.currentFuel = Number(values.currentFuel);
      }
    } else if (values.actionType === "service") {
      if (
        values.serviceCost !== "" &&
        values.serviceCost !== undefined &&
        values.serviceCost !== null
      ) {
        payload.serviceCost = Number(values.serviceCost);
      }
      if (values.serviceDate) {
        const iso = new Date(values.serviceDate).toISOString();
        payload.serviceDate = iso;
      }
    } else if (values.actionType === "assign") {
      if (values.assignDate) {
        const iso = new Date(values.assignDate).toISOString();
        payload.assignDate = iso;
      }
    }

    const { data, error } = await postCarOperation(payload);
    setSubmitting(false);

    // Sequelize-style error?
    const isSequelize =
      data?.error?.name?.includes("Sequelize") ||
      Array.isArray(data?.error?.errors);

    if (isSequelize) {
      const first = data?.error?.errors?.[0];
      const path = first?.path || "actionType";
      const msg = first?.message || data?.message || "Validation error";
      setErrors({ [path]: msg });
      setToast({ open: true, msg: `Error: ${msg}`, sev: "error" });
      return;
    }

    if (data?.message && /exists|invalid|error/i.test(data.message)) {
      setErrors({ actionType: data.message });
      setToast({ open: true, msg: `Error: ${data.message}`, sev: "error" });
      return;
    }

    if (error) {
      setToast({ open: true, msg: `Error: ${error}`, sev: "error" });
      return;
    }

    // Success

    const msg = data?.message || "Car Operation created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();
    // Keep the current actionType selection in the dropdown after reset
    setActionType(values.actionType || "fuel");
  };

  return (
    <>
      <SimpleForm
        key={`users-${users.length}-cars-${cars.length}`} // rebuild when options or actionType changes
        title="CREATE CAR OPERATION"
        subtitle="Log fuel, service, or assignment"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={OP_SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Operation"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
